import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, timer } from 'rxjs';
import { mergeMap, takeUntil } from 'rxjs/operators';
import { ComponentGeneratorService } from 'libs/content-editor/src/lib/services/component-generator.service';
import { ComponentCompilationStatus, EntityComponentState } from '../utils/entity-interfaces';
import { AIChatGeneratorService } from './ai-generator.service';

/**
 * EntityComponentStateService manages component generation and compilation state.
 * Handles component compilation monitoring, bundle fetching, and state tracking.
 * Provides centralized state management for entity component generation lifecycle.
 */
@Injectable({
  providedIn: 'root'
})
export class EntityComponentStateService implements OnDestroy {
  // Component State Management
  private componentStates = new Map<string, BehaviorSubject<EntityComponentState>>();
  private destroy$ = new Subject<void>();

  constructor(
    private componentGeneratorService: ComponentGeneratorService,
    private aiChatGeneratorService: AIChatGeneratorService
  ) {
    // Constructor intentionally empty - dependencies are injected via constructor parameters
  }

  /**
   * Get default component state
   * @returns Default component state object
   */
  private getDefaultState(): EntityComponentState {
    return {
      componentDefinitionId: null,
      compilationId: null,
      draftId: null,
      stepId: null,
      isCompilingLoading: false,
      isBundleLoading: false,
      compilationStatus: null,
      bundleContent: null,
      error: null,
      infoMessage: null,
      isRetrying: false
    };
  }

  /**
   * Get component state observable for a specific message ID
   * @param messageId - Unique identifier for the component state
   * @returns Observable stream of the component state
   */
  getComponentState$(messageId: string): Observable<EntityComponentState> {
    if (!this.componentStates.has(messageId)) {
      this.componentStates.set(messageId, new BehaviorSubject<EntityComponentState>(this.getDefaultState()));
    }
    const state = this.componentStates.get(messageId);
    if (!state) {
      throw new Error(`Component state not found for messageId: ${messageId}`);
    }
    return state.asObservable();
  }

  /**
   * Get current component state synchronously for a specific message ID
   * @param messageId - Unique identifier for the component state
   * @returns Current component state snapshot
   */
  getCurrentComponentState(messageId: string): EntityComponentState {
    const state = this.componentStates.get(messageId);
    return state ? state.value : this.getDefaultState();
  }

  /**
   * Start component generation process with monitoring
   * Sets initial loading states and begins compilation monitoring or bundle fetching
   * @param componentDefinitionId - Unique identifier of the component definition
   * @param compilationId - Unique identifier of the compilation process
   * @param messageId - Unique identifier for the component state tracking
   * @param draftId - Optional draft ID for draft branch bundle
   * @param stepId - Optional step ID for draft branch bundle
   */
  startComponentGeneration(
    componentDefinitionId: string,
    compilationId: string,
    messageId: string,
    draftId?: string,
    stepId?: string
  ): void {
    this.updateComponentState(messageId, {
      componentDefinitionId,
      compilationId,
      draftId: draftId || null,
      stepId: stepId || null,
      isCompilingLoading: true,
      isBundleLoading: false,
      error: null
    });

    if (compilationId === componentDefinitionId) {
      this.loadComponentBundle(componentDefinitionId, messageId);
    } else {
      this.startCompilationMonitoring(compilationId, componentDefinitionId, messageId);
    }
  }

  /**
   * Reset component state to initial values for a specific message ID
   * @param messageId - Unique identifier for the component state to reset
   */
  resetComponentState(messageId: string): void {
    const state = this.componentStates.get(messageId);
    if (state) {
      state.next(this.getDefaultState());
    }
  }

  /**
   * Start monitoring compilation process with polling
   * Monitors compilation status and transitions between loading states
   * @param compilationId - Unique identifier of the compilation process
   * @param componentDefinitionId - Unique identifier of the component definition
   * @param messageId - Unique identifier for the component state tracking
   */
  private startCompilationMonitoring(compilationId: string, componentDefinitionId: string, messageId: string): void {
    const maxRetries = 30;
    const initialInterval = 3000;
    const maxInterval = 900000;

    this.componentGeneratorService.getCompiledComponentStatus(componentDefinitionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (status: unknown) => {
          this.handleCompilationStatus(status as ComponentCompilationStatus, componentDefinitionId, messageId);

          if (!this.isCompilationComplete(status as ComponentCompilationStatus)) {
            this.startPolling(componentDefinitionId, messageId, maxRetries, initialInterval, maxInterval);
          }
        },
        () => {
          this.updateComponentState(messageId, {
            error: 'Component compilation monitoring failed. Please try again.',
            isCompilingLoading: false,
            isBundleLoading: false
          });
        }
      );
  }

  /**
   * Start polling for compilation status updates with exponential backoff
   * @param componentDefinitionId - Unique identifier of the component definition
   * @param messageId - Unique identifier for the component state tracking
   * @param maxRetries - Maximum number of retry attempts
   * @param initialInterval - Initial interval between polling attempts in milliseconds
   * @param maxInterval - Maximum interval between polling attempts in milliseconds
   */
  private startPolling(
    componentDefinitionId: string,
    messageId: string,
    maxRetries: number,
    initialInterval: number,
    maxInterval: number
  ): void {
    let retryCount = 0;

    const poll = () => {
      if (retryCount >= maxRetries) {
        this.updateComponentState(messageId, {
          error: 'Component compilation timeout. Please try again.',
          isCompilingLoading: false,
          isBundleLoading: false
        });
        return;
      }

      retryCount++;

      // Calculate exponential backoff interval
      const currentInterval = Math.min(
        initialInterval * Math.pow(1.2, retryCount - 1),
        maxInterval
      );

      timer(currentInterval)
        .pipe(
          mergeMap(() => this.componentGeneratorService.getCompiledComponentStatus(componentDefinitionId)),
          takeUntil(this.destroy$)
        )
        .subscribe(
          (status: unknown) => {
            this.handleCompilationStatus(status as ComponentCompilationStatus, componentDefinitionId, messageId);

            if (!this.isCompilationComplete(status as ComponentCompilationStatus)) {
              poll();
            }
          },
          () => {
            this.updateComponentState(messageId, {
              error: 'Component compilation monitoring failed. Please try again.',
              isCompilingLoading: false,
              isBundleLoading: false
            });
          }
        );
    };

    poll();
  }


  /**
   * Check if compilation process is complete (successful or failed)
   * @param status - Current compilation status
   * @returns True if compilation is complete (successful or failed)
   */
  private isCompilationComplete(status: ComponentCompilationStatus): boolean {
    return status.status === 'successful' || status.status === 'failure';
  }

  /**
   * Analyze process status to determine retry and progress states
   * @param processes - Array of process status objects
   * @returns Object containing process analysis results
   */
  private analyzeProcessStatus(processes: Array<{
    key: string;
    status: string;
    message: string;
    timestamp: string;
    id: string | null;
  }>): { hasRetryProcess: boolean; hasInProgressProcess: boolean; retryMessages: string[] } {
    const hasRetryProcess = processes.some(process =>
      // TODO: @MustafaDemirhan - message burada bir enm degil; enum olmayan string lerde ya kod isteyelim ya da bu kontrolleri enum a cevirip oraya bakalim
      // status enum, ona bakmak yeterli bu ornekte
      process.status === 'will-be-retried' ||
      process.message?.toLowerCase().includes('will be retried') ||
      process.message?.toLowerCase().includes('retrying')
    );

    const hasInProgressProcess = processes.some(process =>
      process.status === 'in-progress' ||
      process.status === 'running'
    );

    const retryMessages = processes
      .filter(process =>
        process.status === 'will-be-retried' ||
        process.message?.toLowerCase().includes('will be retried') ||
        process.message?.toLowerCase().includes('retrying')
      )
      .map(process => process.message);

    return {
      hasRetryProcess,
      hasInProgressProcess,
      retryMessages
    };
  }

  /**
   * Handle compilation status updates and update component state accordingly
   * Manages state transitions between compilation and bundle loading states
   * @param status - Current compilation status
   * @param componentDefinitionId - Unique identifier of the component definition
   * @param messageId - Unique identifier for the component state tracking
   */
  private handleCompilationStatus(status: ComponentCompilationStatus, componentDefinitionId: string, messageId: string): void {
    const currentState = this.getCurrentComponentState(messageId);

    const draftStepId = status.draftStepId;
    const effectiveStepId = draftStepId || currentState.stepId;

    this.updateComponentState(messageId, {
      compilationStatus: status,
      stepId: effectiveStepId
    });

    // Check processes for detailed status information
    const processStatus = this.analyzeProcessStatus(status.processes);
    if (status.status === 'successful') {
      this.updateComponentState(messageId, {
        isCompilingLoading: false,
        isBundleLoading: true,
        error: null,
        infoMessage: null,
        isRetrying: false
      });

      this.loadComponentBundle(componentDefinitionId, messageId);
    } else if (status.status === 'failure') {
      // Check if any process will be retried
      if (processStatus.hasRetryProcess) {
        this.updateComponentState(messageId, {
          error: null,
          infoMessage: 'Component compilation failed. We\'re now analyzing the error to identify the cause and implement a fix.',
          isCompilingLoading: false,
          isBundleLoading: false,
          isRetrying: true
        });
      } else {
        this.updateComponentState(messageId, {
          error: 'Component compilation failed. Please try again.',
          isCompilingLoading: false,
          isBundleLoading: false,
          infoMessage: null,
          isRetrying: false
        });
      }
    } else if (status.status === 'queued' || status.status === 'in-progress') {
      // Check if there are retry processes even in in-progress state
      if (processStatus.hasRetryProcess) {
        // Show info message about retry even during in-progress
        this.updateComponentState(messageId, {
          isCompilingLoading: true,
          isBundleLoading: false,
          error: null,
          infoMessage: 'Component compilation failed. We\'re now analyzing the error to identify the cause and implement a fix.',
          isRetrying: true
        });
      } else {
        // Normal in-progress state
        this.updateComponentState(messageId, {
          isCompilingLoading: true,
          isBundleLoading: false,
          error: null,
          infoMessage: null,
          isRetrying: false
        });
      }
    } else {
      this.updateComponentState(messageId, {
        isCompilingLoading: true,
        isBundleLoading: false,
        error: null,
        infoMessage: null,
        isRetrying: false
      });
    }
  }

  /**
   * Load component bundle and update state
   * Only tries draft bundle if stepId is available
   * @param componentDefinitionId - Unique identifier of the component definition
   * @param messageId - Unique identifier for the component state tracking
   */
  private loadComponentBundle(componentDefinitionId: string, messageId: string): void {
    const currentState = this.getCurrentComponentState(messageId);
    const stepId = currentState.stepId;

    if (!stepId) {
      this.updateComponentState(messageId, {
        error: 'Step ID is required to load draft bundle. Please wait for compilation to complete.',
        isBundleLoading: false,
        isCompilingLoading: false
      });
      return;
    }

    this.componentGeneratorService.getBranchBundleContent(componentDefinitionId, stepId)
      .subscribe(
        (bundleContent: string) => {
          if (bundleContent && bundleContent.trim().length > 0) {
            this.updateComponentState(messageId, {
              bundleContent,
              isBundleLoading: false,
              isCompilingLoading: false,
              error: null
            });
          } else {
            this.updateComponentState(messageId, {
              error: 'Draft bundle is empty. Please wait for compilation to complete.',
              isBundleLoading: false,
              isCompilingLoading: false
            });
          }
        },
        (error) => {
          console.error('Failed to fetch draft bundle:', error);
          const isNotFoundError = error?.status === 404 || error?.error?.statusCode === 404;
          const errorMessage = isNotFoundError
            ? 'Draft bundle not found. Please wait for compilation to complete.'
            : 'Failed to load draft bundle. Please try again.';
          this.updateComponentState(messageId, {
            error: errorMessage,
            isBundleLoading: false,
            isCompilingLoading: false
          });
        }
      );
  }


  /**
   * Update component state with partial state data for a specific message ID
   * @param messageId - Unique identifier for the component state
   * @param partialState - Partial state data to merge with current state
   */
  private updateComponentState(messageId: string, partialState: Partial<EntityComponentState>): void {
    if (!this.componentStates.has(messageId)) {
      this.componentStates.set(messageId, new BehaviorSubject<EntityComponentState>(this.getDefaultState()));
    }
    const state = this.componentStates.get(messageId);
    if (!state) {
      throw new Error(`Component state not found for messageId: ${messageId}`);
    }
    const currentState = state.value;
    const newState = { ...currentState, ...partialState };
    state.next(newState);
  }

  /**
   * Validate entity component response data for completeness
   * @param responseData - Response data to validate
   * @returns True if response is valid and complete
   */
  validateEntityComponentResponse(responseData: unknown): boolean {
    if (!responseData) {
      return false;
    }

    if (Array.isArray(responseData)) {
      return responseData.length > 0;
    }

    if (typeof responseData === 'object') {
      const obj = responseData as Record<string, unknown>;
      const hasComponentDefinition = obj.componentDefinition && typeof obj.componentDefinition === 'object';
      const hasComponentSample = obj.componentSample && typeof obj.componentSample === 'object';

      if (hasComponentDefinition || hasComponentSample) {
        return true;
      }

      return Object.values(obj).some(value =>
        value !== null && value !== undefined && value !== ''
      );
    }

    return true;
  }

  ngOnDestroy(): void {
    this.componentStates.forEach(state => state.complete());
    this.componentStates.clear();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
